import { debounce } from "lodash"
import { changeListParams, ListControllerProps, Record as RaRecord, ReduxState, useListController } from "ra-core"
import { ListProps } from "ra-core/esm/controller/useListController"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"

const useLocalListController = <RecordType extends RaRecord = RaRecord>(
  props: ListProps,
  localListOptions?: {
    sorters?: Record<string, (a:RecordType, b:RecordType) => number>,
  },
): ListControllerProps<RecordType> => {
  const ctrls = useListController<RecordType>(props)
  const { 
    data, 
    page: innerPage, 
    perPage: innerPerPage,
    currentSort: innerCurrSort,
    resource,
  } = ctrls
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const tapListParams = useCallback((x: any) => {
    x.locationPathname = pathname
    dispatch(changeListParams(resource, x))
    return x
  }, [dispatch, resource, pathname])

  let params = useSelector((reduxState: ReduxState) => reduxState.admin.resources[resource]?.list.params)
  if (!!params?.locationPathname && params.locationPathname !== pathname) {
    params = {}
  }
  const [states, setStates] = useState({
    page: params?.page ?? innerPage ?? 1,
    perPage: params?.perPage ?? innerPerPage,
    sort: params?.sort ?? innerCurrSort.field ?? 'id',
    order: params?.order ?? innerCurrSort.order ?? 'ASC',
    filter: {} as any,
    version: 1,
  })
  const setSortFn = useCallback((sort:string, order?:string) => {
    setStates(v => tapListParams({...v, sort, order: order ?? 'ASC'}))
  }, [tapListParams])

  const filterInfoRef = useRef({ filter: {} as any, displayedFilters: [] as string[]})

  const showFilter = useCallback((filterName:string) => {
    if (Object.keys(filterInfoRef.current.filter).indexOf(filterName) >= 0
    && filterInfoRef.current.displayedFilters.indexOf(filterName) < 0) {
      filterInfoRef.current = { ...filterInfoRef.current, displayedFilters: [...filterInfoRef.current.displayedFilters, filterName] }
      setStates(v => ({...v, version: v.version + 1}))
    }
  }, [])

  const hideFilter = useCallback((filterName:string) => {
    const i = filterInfoRef.current.displayedFilters.indexOf(filterName)
    if (i >= 0) {
      const displayedFilters = [...filterInfoRef.current.displayedFilters]
      displayedFilters.splice(i, 1)
      filterInfoRef.current = { ...filterInfoRef.current, displayedFilters }
      setStates(v => ({...v, version: v.version + 1}))
    }
  }, [])

  const dataArr = Object.values(data)
  const filteredDataArr =
    dataArr.length === 0 || Object.keys(filterInfoRef.current.filter).length === 0
    ? dataArr
    : Object.keys(filterInfoRef.current.filter).reduce((acc, fname) => {
      const fvalue = filterInfoRef.current.filter[fname]
      if (fvalue === undefined || fvalue === null) {
        return acc;
      } else if (typeof fvalue === 'function') {
        return acc.filter(fvalue)
      } else if (typeof fvalue === 'string') {
        const fvalueLow = fvalue.toLowerCase()
        return acc.filter((x) => { 
          try
          {
            return x[fname].toLowerCase().indexOf(fvalueLow) >= 0 
          }
          catch { return false }
        })
      } else {
        return acc.filter(x => x[fname] === fvalue)
      }
    }, dataArr)

  const total = filteredDataArr.length
  
  const sorters = localListOptions?.sorters || {}
  const ids = 
    filteredDataArr.sort((a, b) => {
      const sorter = sorters[states.sort]
      if (!sorter) return -1

      const i = sorter(a, b)
      return states.order === 'DESC' ? -i : i
    })
    .map(x => x.id)
    .splice((states.page - 1) * states.perPage, states.perPage)

  const pageData = ids.reduce((acc, id) => {
    acc[id] = data[id]
    return acc
  }, {} as { [key: string]: RecordType })

  const safeSetPage = useCallback((x:number) => {
    if (states.perPage < 1 || total < 0) return
    const maxPage = total === 0 ? 1 : Math.ceil(total / states.perPage)
    const target = Math.min(Math.max(1, x) , maxPage)
    setStates(v => tapListParams({...v, page: target}))
  }, [states.perPage, total, tapListParams])

  const safeSetPerPage = useCallback((x:number) => {
    const target = Math.max(1, x)
    setStates(v => tapListParams({...v,
      perPage: target,
      page: 1,
    }))
  }, [tapListParams])

  const debouncedSetFilters = useMemo(() => {
    return debounce((filter: any, preservePageLocation?: any) => {
      filterInfoRef.current = { ...filterInfoRef.current, filter }
      setStates(v => tapListParams(preservePageLocation ? {...v, version: v.version + 1} : {...v, page: 1, version: v.version + 1}))
    }, 500)
  }, [tapListParams])

  const setFilters = useCallback((filter: any, displayedFilters?: any, debounce?: boolean, ...rest) => {
    const preservePageLocation = rest[0]
    if (debounce) {
      debouncedSetFilters(filter, preservePageLocation)
    } else {
      filterInfoRef.current = { ...filterInfoRef.current, filter }
      setStates(v => tapListParams(preservePageLocation ? {...v, version: v.version + 1} : {...v, page: 1, version: v.version + 1}))
    }
  }, [debouncedSetFilters, tapListParams])

  useEffect(() => {
    if (ids.length === 0 && states.page > 1) {
      setStates(v => tapListParams({...v, page: 1}))
    }
  }, [states.page, ids.length, tapListParams])

  return {
    ...ctrls,
    page: states.page,
    setPage: safeSetPage,
    perPage: states.perPage,
    setPerPage: safeSetPerPage,
    currentSort: { field: states.sort, order: states.order || 'ASC' },
    setSort: setSortFn,
    setFilters,
    showFilter,
    hideFilter,
    displayedFilters: filterInfoRef.current.displayedFilters,
    filterValues: filterInfoRef.current.filter,
    data: pageData,
    ids,
    total,
  }
}

export default useLocalListController;
